import { defineComponent as _defineComponent } from 'vue'
import { getDebtorsList, getDebtorsTotal, updateDebtorComment, updateDebtorPaymentCalendar } from "@/api/revenue";
import SvgEdit from "@/assets/icons/edit-pen-white.svg";
import SvgPlus from "@/assets/icons/plus.svg";
import BaseTable from "@/components-UI/table/BaseTable.vue";
import NumberCell from "@/components-UI/table/cells/NumberCell.vue";
import DebtorsChipPopup from "@/components_v3/revenue/debtors/DebtorsChipPopup.vue";
import { useDebtors } from "@/services/TablePage/debtors";
import store from "@/store";
import { useDebtorsStore } from "@/store_v3/DebtorsStore";
import { showServerError } from "@/utils";
import { FormattedItem } from "@/utils/CellsFromBff";
import { getCurrentDate, getFormatDate } from "@/utils/formatDates";
import { storeToRefs } from "pinia";
import { computed, onMounted, ref, watch, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router/composables";
import BaseTextarea from "../../../components-UI/BaseTextarea.vue";
import BaseButtonV2 from "../../../components-UI/buttons/BaseButtonV2.vue";
import BaseModal from "../../../components-UI/modals/BaseModal.vue";
import { USER_ROLES } from "../../../mappings/user-roles";
import { SUMMARY_KEYS } from "@/services/TablePage/debtors";
import { formatMinutesToDuration } from "@/utils/formatDates";
import { Debtor, DebtorOtherService, DebtorPayment, DebtorPaymentCalendar } from "@crm-ui/types";
import BaseDateTimePicker from "@/components-UI/BaseDateTimePicker.vue";
import BaseInputNumber from "@/components-UI/BaseInputNumber.vue";
import TrashButton from "@/components-UI/buttons/TrashButton.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'DebtorsTable',
  setup(__props) {

const { headers, summaryHeaders } = useDebtors();
const { isNeedUpdateDebtors } = storeToRefs(useDebtorsStore());
const route = useRoute();
const router = useRouter();
const isLoading = ref(false);
const isLoadingTotal = ref(false);
const items = ref<FormattedItem<Debtor>[]>([]);
const totalItems = ref([]);
const total = ref(0);
const offset = ref(0);
const tableEl = ref<BaseTable | null>(null);
const totalTableEl = ref<BaseTable | null>(null);
const tableWrap = ref<HTMLDivElement | null>(null);
const isShowEditCommentModal = ref(false);
const editableItem = ref<Debtor | null>(null);
const comment = ref("");
const isShowEditPaymentCalendarModal = ref(false);
const paymentCalendar = ref<DebtorPaymentCalendar[] | null>(null);

const getTotalTableSlots = computed(() => {
    return summaryHeaders
        .filter((h) => h.value !== "currency")
        .map((h) => ({
            slot: `item.${h.value}`,
            key: h.value,
        }));
});

watchEffect(() => {
    if (isNeedUpdateDebtors.value) {
        getDebtors();
        isNeedUpdateDebtors.value = false;
    }
});

onMounted(() => {
    ensureSortInUrl();
    commitSortToVuex();
    getDebtors();
    if (tableEl.value) {
        tableWrap.value = tableEl.value.$refs.tableWrap;
    }
});

watch(
    () => route.query.columns,
    () => {
        getDebtors();
    },
);

const DEFAULT_SORT_COLUMN = 'created_at';
const DEFAULT_SORT_DIRECTION = 'desc';

function normalizeSortDirection(dir: string | string[] | undefined): string {
    const v = Array.isArray(dir) ? dir[0] : dir;
    if (!v) return DEFAULT_SORT_DIRECTION;
    const d = String(v).toLowerCase();
    return d === 'asc' || d === 'desc' ? d : DEFAULT_SORT_DIRECTION;
}

function getSortFromRoute() {
    const col = (route.query.sorting_column as string) || DEFAULT_SORT_COLUMN;
    const dir = normalizeSortDirection(route.query.sorting_direction as string);
    return { sorting_column: col, sorting_direction: dir };
}

function hasSortInQuery(): boolean {
    const col = route.query.sorting_column;
    const dir = route.query.sorting_direction;
    const c = Array.isArray(col) ? col[0] : col;
    const d = Array.isArray(dir) ? dir[0] : dir;
    return Boolean(c && d);
}

function ensureSortInUrl() {
    if (hasSortInQuery()) return;
    const { sorting_column, sorting_direction } = getSortFromRoute();
    router.replace({
        query: {
            ...route.query,
            sorting_column,
            sorting_direction,
        },
    });
}

function commitSortToVuex() {
    const { sorting_column, sorting_direction } = getSortFromRoute();
    const prev = store.getters['GET_FILTERS_PARAMS_OBJECT'] || {};
    store.commit('SET_FILTERS_PARAMS_OBJECT', {
        ...prev,
        sorting_column,
        sorting_direction,
    });
}

// --------- COMMENTS
function isShowEditComment(item: Debtor) {
    const user = store.getters["authModule/GET_USER"];

    const adminRoles = [USER_ROLES.ADMIN, USER_ROLES.FINANCIER, USER_ROLES.SUPERVISOR];
    const isAdmin = adminRoles.some((role) => user.roles.includes(role));

    const isManager = user.roles.includes(USER_ROLES.ACCOUNT_MANAGER);
    const isCreateByManager = item.created_by === `${user.first_name} ${user.last_name}`;

    return isAdmin || (isManager && isCreateByManager);
}

function showEditCommentModal(item: Debtor) {
    editableItem.value = { ...item };
    comment.value = editableItem.value.comment_crm ?? "";
    isShowEditCommentModal.value = true;
}

function closeEditCommentModal() {
    isShowEditCommentModal.value = false;
    editableItem.value = null;
    comment.value = "";
}

async function saveComment() {
    try {
        await updateDebtorComment(editableItem.value!.payment_invoice_id, { comment: comment.value });
        closeEditCommentModal();
        await getDebtors();
    } catch (err) {
        showServerError(err, "Комментарий не сохранен");
    }
}


// --------- PAYMENT CALENDAR
const isShowEditPaymentCalendar = computed(() => {
    const user = store.getters["authModule/GET_USER"];
    return [USER_ROLES.ADMIN, USER_ROLES.FINANCIER, USER_ROLES.SUPERVISOR, USER_ROLES.FINANCIER].some((role) => user.roles.includes(role));
});

const getTotal = computed(() => {
    return (editableItem.value?.total ?? 0).toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
});
const getCurrency = computed(() => {
    const cur = editableItem.value?.currency ?? '';
    return store.getters['catalogueModule/GET_CURRENT_CURRENCY_ICON'](cur);
});

function showEditPaymentCalendar(item: Debtor) {
    editableItem.value = { ...item };
    if (!editableItem.value.payment_calendars.length) {
        paymentCalendar.value = [{ id: 0, amount: editableItem.value.total, percentage: 100, date: getCurrentDate('yyyy-MM-dd') }];
    } else {
        paymentCalendar.value = editableItem.value.payment_calendars.map(cal => ({ ...cal }));
    }
    isShowEditPaymentCalendarModal.value = true;
}

function closeEditPaymentCalendar() {
    isShowEditPaymentCalendarModal.value = false;
    editableItem.value = null;
    paymentCalendar.value = null;
}

const isDisableAdd = computed(() => {
    const percent = paymentCalendar.value?.reduce((acc, item) => acc + item.percentage, 0) ?? 0;
    return percent >= 100;
});

function deletePaymentCalendar(index: number) {
    if (!paymentCalendar.value) return;
    paymentCalendar.value.splice(index, 1);
}

function addPaymentCalendar() {
    if (!paymentCalendar.value) return;
    const total = editableItem.value?.total ?? 0;
    const amountSum = paymentCalendar.value.reduce((acc, item) => acc + item.amount, 0);
    const amount = total - amountSum;
    const percentage = Math.round((amount / total) * 100);
    paymentCalendar.value.push({ id: 0, amount, percentage, date: getCurrentDate('yyyy-MM-dd') });
}

function updateDate(item: DebtorPaymentCalendar, value: string) {
    item.date = value === '' ? getCurrentDate('yyyy-MM-dd') : value;
}

function round2(n: number): number {
    return Math.round(n * 100) / 100;
}

function updatePercentage(item: DebtorPaymentCalendar, value: number) {
    item.percentage = round2(value);
    const total = editableItem.value?.total ?? 0;
    item.amount = round2((total * value) / 100);
}

function updateAmount(item: DebtorPaymentCalendar, value: number) {
    item.amount = round2(value);
    const total = editableItem.value?.total ?? 0;
    item.percentage = round2((value / total) * 100);
}

const isDisableSave = computed(() => {
    const percentage = paymentCalendar.value?.reduce((acc, item) => acc + item.percentage, 0) ?? 0;
    const isPercentInvalid = percentage > 100;

    const isZero = paymentCalendar.value?.some(i => i.percentage === 0 || i.amount === 0);

    return isPercentInvalid || isZero;
});

async function savePaymentCalendar() {
    try {
        await updateDebtorPaymentCalendar(editableItem.value!.payment_invoice_id, { items: paymentCalendar.value! });
        closeEditPaymentCalendar();
        await getDebtors();
    } catch (err) {
        showServerError(err, "Календарь не сохранен");
    }
}

const changePage = (offsetValue: number) => {
    offset.value = offsetValue;
    getDebtors();
};

const getDebtors = async () => {
    if (isLoading.value) return;
    try {
        isLoading.value = true;
        const params = getParams();
        const { data, meta } = await getDebtorsList(params);
        await getDebtorsTotalList();
        total.value = meta?.total;
        items.value = data;
    } catch (err) {
        showServerError(err, "Список должников не загружен");
    } finally {
        isLoading.value = false;
        items.value.length && calculateHeight();
    }
};

const getDebtorsTotalList = async () => {
    if (isLoadingTotal.value) return;
    try {
        isLoadingTotal.value = true;
        const params = getParams();
        totalItems.value = await getDebtorsTotal(params);
    } catch (err) {
        showServerError(err, "Список сумм должников не загружен");
    } finally {
        isLoadingTotal.value = false;
    }
};

const getParams = () => {
    const params = { ...store.getters['GET_FILTERS_PARAMS_OBJECT'] };
    params.columns = route.query.columns;
    params.offset = offset.value;
    params.limit = 100;

    const { sorting_column, sorting_direction } = getSortFromRoute();
    params.sorting_column = sorting_column;
    params.sorting_direction = sorting_direction;

    return params;
};

const parseToLocalString = (num: number): string => {
    return num.toLocaleString("ru-RU", {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
};

const getOnePaymentText = (item: Debtor): string => {
    const payment = item.payments?.[0];
    if (!payment) return "";

    const { currency, amount, date } = payment;
    const formatDate = getFormatDate(date, "dd.MM.yyyy");

    return `${parseToLocalString(amount)} ${currency} : ${formatDate}`;
};

const getPayment = (item: DebtorPayment) => {
    if (!item) return "";
    const date = getFormatDate(item.date, "dd.MM.yyyy");
    return item && item.amount ?
        `${item.amount ? parseToLocalString(item.amount) : "0,00"
        } ${item.currency || ""} : ${date || ""}` : "0,00";
};
const isShowPayments = (item: Debtor) => {
    return item.payments && item.payments.length && item.payments.length > 1;
};
const getActivatorTextPayments = (item: Debtor) => {
    const amount = item.payments.reduce((sum, i) => sum + i.amount, 0);
    const currency = item.payments.at(-1)?.currency ?? '';
    return `${parseToLocalString(amount)} ${currency}`;
};
const getPaymentsItems = (item: Debtor) => {
    return item.payments ? item.payments.map((payment) => getPayment(payment)) : [];
};

const getServiceNames = (item: DebtorOtherService) => {
    if (!item) return "";
    return item.service_name ? item.service_name : "";
};
const isShowServices = (item: Debtor) => {
    return item.other_services && item.other_services.length && getActivatorTextServices(item);
};
const getActivatorTextServices = (item: Debtor) => {
    const service = item.other_services ? item.other_services.find((service) => !!service.service_name) : null;
    return service ? getServiceNames(service) : "";
};
const getServicesItems = (item: Debtor) => {
    return item.other_services ? item.other_services.map((service) => getServiceNames(service)) : [];
};

const calculateHeight = () => {
    const mainTable = tableEl.value?.$el as HTMLElement;
    const summaryTable = totalTableEl.value?.$el as HTMLElement;

    if (mainTable && summaryTable) {
        mainTable.style.setProperty("--summary-table-height", `${summaryTable.clientHeight}px`);
    }
};

watch(
    () => [route.query.sorting_column, route.query.sorting_direction],
    () => {
        if (!hasSortInQuery()) {
            ensureSortInUrl();
            return;
        }
        commitSortToVuex();
        offset.value = 0;
        getDebtors();
    },
);


return { __sfc: true,headers, summaryHeaders, isNeedUpdateDebtors, route, router, isLoading, isLoadingTotal, items, totalItems, total, offset, tableEl, totalTableEl, tableWrap, isShowEditCommentModal, editableItem, comment, isShowEditPaymentCalendarModal, paymentCalendar, getTotalTableSlots, DEFAULT_SORT_COLUMN, DEFAULT_SORT_DIRECTION, normalizeSortDirection, getSortFromRoute, hasSortInQuery, ensureSortInUrl, commitSortToVuex, isShowEditComment, showEditCommentModal, closeEditCommentModal, saveComment, isShowEditPaymentCalendar, getTotal, getCurrency, showEditPaymentCalendar, closeEditPaymentCalendar, isDisableAdd, deletePaymentCalendar, addPaymentCalendar, updateDate, round2, updatePercentage, updateAmount, isDisableSave, savePaymentCalendar, changePage, getDebtors, getDebtorsTotalList, getParams, parseToLocalString, getOnePaymentText, getPayment, isShowPayments, getActivatorTextPayments, getPaymentsItems, getServiceNames, isShowServices, getActivatorTextServices, getServicesItems, calculateHeight, SvgEdit, SvgPlus, BaseTable, NumberCell, DebtorsChipPopup, getFormatDate, BaseTextarea, BaseButtonV2, BaseModal, SUMMARY_KEYS, formatMinutesToDuration, BaseDateTimePicker, BaseInputNumber, TrashButton }
}

})